// Main changes:
// 1. Added useTheme/useMediaQuery for responsive header like ReportStudentPersonalInformation.
// 2. Added tableWrapSx + inner horizontal scroll wrapper.
// 3. Root paper is white/rounded with blue border.
// 4. Section cards use orange left stripe.
// 5. Buttons, inputs, table header, zebra rows match your report theme.

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Table,
  Box,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  Select,
  MenuItem,
  Typography,
  Alert,
  CircularProgress,
  LinearProgress,
  Snackbar,
  Divider,
  Stack,
  Collapse,
  Chip,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useNavigate } from "react-router-dom";
import { API_URL } from "./config";

const THEME_ORANGE = "#F07828";
const THEME_ORANGE_2 = "#ed7f35ff";
const BG = "#F2F2F2";
const BORDER = "rgba(25,118,210,0.20)";
const BORDER_HOVER = "rgba(25,118,210,0.45)";
const BORDER_FOCUS = "rgba(240,120,40,0.80)";
const ROW_ALT = "rgba(25,118,210,0.06)";
const ROW_HOVER = "rgba(25,118,210,0.10)";

const ApproverStudentApplication = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));

  const [data, setData] = useState([]);
  const [editedData, setEditedData] = useState({});
  const [expandedRows, setExpandedRows] = useState({});
  const [loading, setLoading] = useState(true);
  const [logincampusCode, setCampusCode] = useState("");
  const [loginusername, setUsername] = useState("");
  const [semesterOptions, setSemesterOptions] = useState([]);
  const [academicYearOptions, setAcademicYearOptions] = useState([]);
  const [currentAcademicYear, setCurrentAcademicYear] = useState("");
  const [err, setErr] = useState("");
  const [savingId, setSavingId] = useState(null);
  const [toast, setToast] = useState({ open: false, type: "success", msg: "" });

  const token = localStorage.getItem("auth");

  const authHeader = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  const pageWrapSx = { p: 2, backgroundColor: BG };

  const rootPaperSx = {
    mx: "auto",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
    bgcolor: "#fff",
  };

  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    color: "white",
    display: "flex",
    alignItems: isSmDown ? "flex-start" : "center",
    justifyContent: "space-between",
    gap: 2,
    flexDirection: isSmDown ? "column" : "row",
  };

  const bodySx = { p: 2, backgroundColor: BG };

  const surfaceSx = {
    borderRadius: 2,
    backgroundColor: "#fff",
    border: `1px solid ${BORDER}`,
  };

  const sectionSx = {
    ...surfaceSx,
    p: 2,
    position: "relative",
    overflow: "hidden",
    "&:before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: 6,
      background: `linear-gradient(180deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
      opacity: 0.95,
    },
  };

  const inputSx = {
    "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_HOVER },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_FOCUS },
    "& .MuiInputLabel-root.Mui-focused": { color: THEME_ORANGE },
  };

  const selectSx = {
    ...inputSx,
    "& .MuiSelect-select": { py: 1.05 },
  };

  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: THEME_ORANGE,
    "&:hover": { backgroundColor: THEME_ORANGE_2 },
    whiteSpace: "nowrap",
  };

  const headerOutlinedBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    color: "white",
    borderColor: "rgba(255,255,255,0.70)",
    "&:hover": {
      borderColor: "white",
      backgroundColor: "rgba(255,255,255,0.12)",
    },
    whiteSpace: "nowrap",
  };

  const tableWrapSx = {
    ...surfaceSx,
    overflow: "hidden",
  };

  const theadRowSx = {
    backgroundColor: "rgba(25,118,210,0.95)",
    "& th": {
      color: "#fff",
      fontWeight: 900,
      borderBottom: "1px solid rgba(255,255,255,0.18)",
      whiteSpace: "nowrap",
      py: 1,
    },
  };

  const zebraRowSx = (i) => ({
    backgroundColor: i % 2 === 0 ? ROW_ALT : "#fff",
    cursor: "pointer",
    "&:hover": { backgroundColor: ROW_HOVER },
    "& td": { borderBottom: "1px solid rgba(25,118,210,0.10)", py: 0.75 },
  });

  const fixedGridSx = (cols) => ({
    display: "grid",
    gridTemplateColumns: {
      xs: "1fr",
      sm: `repeat(${Math.min(cols, 2)}, 1fr)`,
      md: `repeat(${Math.min(cols, 4)}, 1fr)`,
      lg: `repeat(${cols}, 1fr)`,
    },
    gap: 1.5,
  });

  const getFinalSemester = (appId, original) =>
    editedData[appId]?.Semester ??
    original?.Semester ??
    (semesterOptions[0]?.value ?? "");

  const getTodayIST = () => dayjs().format("YYYY-MM-DD");

  const getEditedValue = (item, field, fallback = "") => {
    if (!item?.ApplicationID) return fallback;

    if (field === "AdmissionDate") {
      return editedData[item.ApplicationID]?.AdmissionDate || getTodayIST();
    }

    return editedData[item.ApplicationID]?.[field] ?? item[field] ?? fallback;
  };

  useEffect(() => {
    if (!token) return;

    try {
      const decodedToken = JSON.parse(atob(token.split(".")[1]));
      setCampusCode(decodedToken.campusCode || decodedToken.CampusCode || "");
      setUsername(decodedToken.username || decodedToken.Username || "");
    } catch (e) {
      console.error("Error decoding token:", e);
      setToast({ open: true, type: "error", msg: "Invalid session token." });
    }
  }, [token]);

  useEffect(() => {
    const fetchLookups = async () => {
      if (!logincampusCode) return;

      try {
        const res = await axios.get(`${API_URL}/api/lookups/all`, authHeader);
        const lookups = res.data || {};

        setAcademicYearOptions(lookups.AcademicYear || []);
        setCurrentAcademicYear(lookups.CurrentAcademicYear?.[0] || "");
      } catch (e) {
        console.error("Failed to load academic years:", e);
        setAcademicYearOptions([]);
        setCurrentAcademicYear("");
      }
    };

    fetchLookups();
  }, [logincampusCode, authHeader]);

  const fetchApplications = async () => {
    if (!logincampusCode) return;

    setErr("");
    setLoading(true);

    try {
      const response = await axios.get(`${API_URL}/api/ApproverStudentApplication`, {
        ...authHeader,
        params: { CampusCode: logincampusCode },
      });

      setData(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching data:", error);
      if (error.response?.status === 401) navigate("/");
      setErr("Failed to load applications.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApplications();
  }, [logincampusCode, navigate, authHeader]);

  useEffect(() => {
    const fetchSemesters = async () => {
      if (!logincampusCode) return;

      try {
        const res = await axios.get(`${API_URL}/api/getSemesters`, {
          ...authHeader,
          params: { CampusCode: logincampusCode },
        });

        const rows = Array.isArray(res.data) ? res.data : [];
        setSemesterOptions(
          rows.map((s) => {
            const value = s.Semester ?? s.SemesterNumber ?? s.SemesterID ?? s.Value;
            const label = s.SemesterName ?? String(value);
            return { value, label };
          })
        );
      } catch (e) {
        console.error("Failed to load semesters", e);
        setSemesterOptions([]);
      }
    };

    fetchSemesters();
  }, [logincampusCode, authHeader]);

  const formatDateOnly = (value) => {
    if (!value) return "";
    const str = String(value);
    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
    const iso = str.match(/^(\d{4}-\d{2}-\d{2})T/);
    if (iso) return iso[1];
    return str;
  };

  const handleFieldChange = (ApplicationID, field, value) => {
    setEditedData((prev) => {
      const nextForRow = {
        ...prev[ApplicationID],
        [field]: field === "IsStudentActive" ? (value === "Yes" ? "Yes" : "No") : value,
      };

      if (field === "Entry" && value === "Regular" && semesterOptions.length) {
        nextForRow.Semester = semesterOptions[0].value;
      }

      return { ...prev, [ApplicationID]: nextForRow };
    });
  };

  const renderField = (label, value) => (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography sx={{ fontWeight: 700, wordBreak: "break-word" }}>
        {value || "-"}
      </Typography>
    </Box>
  );

  const renderDocumentsSummary = (docs = []) => {
    if (!docs.length) return <Chip size="small" label="No documents" />;

    const verified = docs.filter((d) => d.IsVerified).length;

    return (
      <Stack spacing={0.5}>
        <Chip
          size="small"
          label={`${docs.length} document(s)`}
          sx={{ bgcolor: "rgba(25,118,210,0.08)", fontWeight: 800 }}
        />
        <Typography variant="caption" color="text.secondary">
          Verified: {verified}/{docs.length}
        </Typography>
      </Stack>
    );
  };

  const renderEducationCards = (rows = []) => {
    if (!rows.length) {
      return <Alert severity="info" sx={{ borderRadius: 2 }}>No education details found.</Alert>;
    }

    return (
      <Stack spacing={1.5}>
        {rows.map((edu, idx) => (
          <Paper key={`edu-${idx}`} variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: BORDER }}>
            <Box sx={fixedGridSx(9)}>
              {renderField("Level", edu.EducationLevel)}
              {renderField("Board Type", edu.BoardType)}
              {renderField("Institution", edu.InstitutionName)}
              {renderField("Hall Ticket", edu.HallTicketNumber)}
              {renderField("Percentage", edu.Percentage)}
              {renderField("CGPA", edu.CGPA)}
              {renderField("City", edu.InstitutionCity)}
              {renderField("State", edu.InstitutionState)}
              {renderField("Passed Out Year", edu.PassedOutYear)}
            </Box>
          </Paper>
        ))}
      </Stack>
    );
  };

  const renderCompetitiveExamCards = (rows = []) => {
    if (!rows.length) {
      return <Alert severity="info" sx={{ borderRadius: 2 }}>No competitive exam details found.</Alert>;
    }

    return (
      <Stack spacing={1.5}>
        {rows.map((exam, idx) => (
          <Paper key={`exam-${idx}`} variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: BORDER }}>
            <Box sx={fixedGridSx(5)}>
              {renderField("Exam Type", exam.ExamType)}
              {renderField("Hall Ticket", exam.HallTicketNumber)}
              {renderField("Rank", exam.RankNo)}
              {renderField("Score", exam.Score)}
              {renderField("Year", exam.ExamYear)}
            </Box>
          </Paper>
        ))}
      </Stack>
    );
  };

  const renderDocumentCards = (rows = []) => {
    if (!rows.length) {
      return <Alert severity="info" sx={{ borderRadius: 2 }}>No document records found.</Alert>;
    }

    return (
      <Stack spacing={1.5}>
        {rows.map((doc, idx) => (
          <Paper key={`doc-${idx}`} variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: BORDER }}>
            <Box sx={fixedGridSx(4)}>
              {renderField("Document Type", doc.DocumentType)}
              {renderField("Received", doc.IsReceived ? "Yes" : "No")}
              {renderField("Verified", doc.IsVerified ? "Yes" : "No")}
              {renderField("Comments", doc.Comments)}
            </Box>
          </Paper>
        ))}
      </Stack>
    );
  };

  const handleSave = async (ApplicationID) => {
    const originalData = data.find((i) => i.ApplicationID === ApplicationID);
    const updatedFieldsRaw = editedData[ApplicationID] || {};
    const finalSemester = getFinalSemester(ApplicationID, originalData);
    const updatedFields = { ...updatedFieldsRaw, Semester: finalSemester };

    const updatedData = {
      ...originalData,
      ...updatedFields,
      loginusername,
    };

    const requiredFields = [
      "Entry",
      "Semester",
      "AcademicYear",
      "Qualification",
      "AdmissionType",
      "HostelType",
    ];

    const missingFields = requiredFields.filter((f) => !updatedData[f]);

    if (missingFields.length) {
      alert(`Please fill all the required fields: ${missingFields.join(", ")}`);
      return;
    }

    try {
      setSavingId(ApplicationID);
      setErr("");

      const response = await axios.post(
        `${API_URL}/api/updateStudentApplication`,
        updatedData,
        authHeader
      );

      if (response.status === 200) {
        setToast({
          open: true,
          type: "success",
          msg: "Student application updated successfully.",
        });

        setData((prev) =>
          prev.map((row) =>
            row.ApplicationID === ApplicationID ? { ...row, ...updatedFields } : row
          )
        );

        setEditedData(({ [ApplicationID]: _remove, ...rest }) => rest);
      }
    } catch (error) {
      console.error("Error updating application:", error);
      setToast({ open: true, type: "error", msg: "Save failed." });
    } finally {
      setSavingId(null);
    }
  };

  const reload = async () => {
    setEditedData({});
    setExpandedRows({});
    await fetchApplications();
    setToast({ open: true, type: "success", msg: "Reloaded." });
  };

  const expandedItem = data.find((x) => expandedRows[x.ApplicationID]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={pageWrapSx}>
        <Paper elevation={5} sx={rootPaperSx}>
          <Box sx={headerSx}>
            <Box>
              <Typography sx={{ fontSize: "1.1rem", fontWeight: 900, lineHeight: 1.2 }}>
                Approver Student Application
              </Typography>
              <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
                Click a row to expand details, then approve or reject applications.
              </Typography>
            </Box>

            <Button
              fullWidth={isSmDown}
              variant="outlined"
              onClick={reload}
              disabled={!logincampusCode || loading}
              sx={headerOutlinedBtnSx}
            >
              {loading ? <CircularProgress size={18} sx={{ color: "white" }} /> : "Reload"}
            </Button>
          </Box>

          <Box sx={bodySx}>
            {err && (
              <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                {err}
              </Alert>
            )}

            {loading && (
              <Box sx={{ mb: 2 }}>
                <LinearProgress
                  sx={{
                    height: 8,
                    borderRadius: 999,
                    backgroundColor: "rgba(25,118,210,0.10)",
                    "& .MuiLinearProgress-bar": {
                      backgroundColor: THEME_ORANGE,
                      borderRadius: 999,
                    },
                  }}
                />
              </Box>
            )}

            <Paper variant="outlined" sx={sectionSx}>
              <Box sx={{ pl: 1 }}>
                <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>
                  Applications
                </Typography>

                <Paper elevation={0} sx={tableWrapSx}>
                  <Box sx={{ overflowX: "auto", overflowY: "hidden" }}>
                    <TableContainer component="div">
                      {loading ? (
                        <Typography sx={{ textAlign: "center", py: 2 }}>Loading...</Typography>
                      ) : data.length === 0 ? (
                        <Typography sx={{ textAlign: "center", py: 2 }}>No records found</Typography>
                      ) : (
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={theadRowSx}>
                              {[
                                "",
                                "Application ID",
                                "Entry",
                                "Academic Year",
                                "Semester",
                                "Qualification",
                                "Admission Type",
                                "Hostel Type",
                                "Active",
                                "Documents",
                                "Docs Verified",
                                "Admission Date",
                                "App Status",
                                "Actions",
                              ].map((h) => (
                                <TableCell key={h}>{h}</TableCell>
                              ))}
                            </TableRow>
                          </TableHead>

                          <TableBody>
                            {data.map((item, idx) => {
                              const isExpanded = !!expandedRows[item.ApplicationID];
                              const semValue = getFinalSemester(item.ApplicationID, item);

                              return (
                                <TableRow
                                  key={item.ApplicationID}
                                  sx={zebraRowSx(idx)}
                                  onClick={() =>
                                    setExpandedRows((prev) => {
                                      const isOpen = !!prev[item.ApplicationID];
                                      return isOpen ? {} : { [item.ApplicationID]: true };
                                    })
                                  }
                                >
                                  <TableCell>{isExpanded ? "▾" : "▸"}</TableCell>
                                  <TableCell>{item.ApplicationID}</TableCell>

                                  <TableCell sx={{ minWidth: 130 }} onClick={(e) => e.stopPropagation()}>
                                    <Select
                                      size="small"
                                      fullWidth
                                      sx={selectSx}
                                      value={getEditedValue(item, "Entry")}
                                      onChange={(e) =>
                                        handleFieldChange(item.ApplicationID, "Entry", e.target.value)
                                      }
                                    >
                                      <MenuItem value="Regular">Regular</MenuItem>
                                      <MenuItem value="Lateral">Lateral</MenuItem>
                                      <MenuItem value="Other">Other</MenuItem>
                                    </Select>
                                  </TableCell>

                                  <TableCell sx={{ minWidth: 160 }} onClick={(e) => e.stopPropagation()}>
                                    <Select
                                      size="small"
                                      fullWidth
                                      sx={selectSx}
                                      value={getEditedValue(item, "AcademicYear")}
                                      onChange={(e) =>
                                        handleFieldChange(item.ApplicationID, "AcademicYear", e.target.value)
                                      }
                                    >
                                      <MenuItem value="">Select</MenuItem>
                                    
                                      {academicYearOptions.map((year) => (
                                        <MenuItem key={year} value={year}>
                                          {year}
                                          {year === currentAcademicYear ? " Current" : ""}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </TableCell>

                                  <TableCell sx={{ minWidth: 140 }} onClick={(e) => e.stopPropagation()}>
                                    <Select
                                      size="small"
                                      fullWidth
                                      sx={selectSx}
                                      value={semValue}
                                      onChange={(e) =>
                                        handleFieldChange(item.ApplicationID, "Semester", e.target.value)
                                      }
                                    >
                                      {semesterOptions.map((opt) => (
                                        <MenuItem key={opt.value} value={opt.value}>
                                          {opt.label}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </TableCell>

                                  <TableCell sx={{ minWidth: 135 }} onClick={(e) => e.stopPropagation()}>
                                    <Select
                                      size="small"
                                      fullWidth
                                      sx={selectSx}
                                      value={getEditedValue(item, "Qualification")}
                                      onChange={(e) =>
                                        handleFieldChange(item.ApplicationID, "Qualification", e.target.value)
                                      }
                                    >
                                      <MenuItem value="Tenth">Tenth</MenuItem>
                                      <MenuItem value="Inter">Inter</MenuItem>
                                      <MenuItem value="Degree">Degree</MenuItem>
                                      <MenuItem value="Other">Other</MenuItem>
                                    </Select>
                                  </TableCell>

                                  <TableCell sx={{ minWidth: 150 }} onClick={(e) => e.stopPropagation()}>
                                    <Select
                                      size="small"
                                      fullWidth
                                      sx={selectSx}
                                      value={getEditedValue(item, "AdmissionType")}
                                      onChange={(e) =>
                                        handleFieldChange(item.ApplicationID, "AdmissionType", e.target.value)
                                      }
                                    >
                                      <MenuItem value="Councelling">Councelling</MenuItem>
                                      <MenuItem value="Other">Other</MenuItem>
                                    </Select>
                                  </TableCell>

                                  <TableCell sx={{ minWidth: 140 }} onClick={(e) => e.stopPropagation()}>
                                    <Select
                                      size="small"
                                      fullWidth
                                      sx={selectSx}
                                      value={getEditedValue(item, "HostelType")}
                                      onChange={(e) =>
                                        handleFieldChange(item.ApplicationID, "HostelType", e.target.value)
                                      }
                                    >
                                      <MenuItem value="Hosteller">Hosteller</MenuItem>
                                      <MenuItem value="Day Scholar">Day Scholar</MenuItem>
                                    </Select>
                                  </TableCell>

                                  <TableCell sx={{ minWidth: 110 }} onClick={(e) => e.stopPropagation()}>
                                    <Select
                                      size="small"
                                      fullWidth
                                      sx={selectSx}
                                      value={getEditedValue(item, "IsStudentActive", item.IsStudentActive ? "Yes" : "No")}
                                      onChange={(e) =>
                                        handleFieldChange(item.ApplicationID, "IsStudentActive", e.target.value)
                                      }
                                    >
                                      <MenuItem value="Yes">Yes</MenuItem>
                                      <MenuItem value="No">No</MenuItem>
                                    </Select>
                                  </TableCell>

                                  <TableCell>{renderDocumentsSummary(item.DocumentDetails || [])}</TableCell>

                                  <TableCell>
                                    <Chip
                                      size="small"
                                      label={item.DocumentsVerified || "Pending"}
                                      sx={{
                                        fontWeight: 800,
                                        bgcolor:
                                          item.DocumentsVerified === "Verified"
                                            ? "rgba(46,125,50,0.10)"
                                            : "rgba(240,120,40,0.12)",
                                      }}
                                    />
                                  </TableCell>

                                  <TableCell sx={{ minWidth: 190 }} onClick={(e) => e.stopPropagation()}>
                                    <DatePicker
                                      format="YYYY-MM-DD"
                                      value={dayjs(formatDateOnly(getEditedValue(item, "AdmissionDate")))}
                                      onChange={(newValue) =>
                                        handleFieldChange(
                                          item.ApplicationID,
                                          "AdmissionDate",
                                          newValue ? newValue.format("YYYY-MM-DD") : ""
                                        )
                                      }
                                      slotProps={{
                                        textField: {
                                          size: "small",
                                          fullWidth: true,
                                          sx: inputSx,
                                        },
                                      }}
                                    />
                                  </TableCell>

                                  <TableCell sx={{ minWidth: 150 }} onClick={(e) => e.stopPropagation()}>
                                    <Select
                                      size="small"
                                      fullWidth
                                      sx={selectSx}
                                      value={getEditedValue(item, "ApplicationStatus", "Pending")}
                                      onChange={(e) =>
                                        handleFieldChange(item.ApplicationID, "ApplicationStatus", e.target.value)
                                      }
                                    >
                                      <MenuItem value="Pending">Pending</MenuItem>
                                      <MenuItem value="Approved">Approved</MenuItem>
                                      <MenuItem value="Rejected">Rejected</MenuItem>
                                    </Select>
                                  </TableCell>

                                  <TableCell onClick={(e) => e.stopPropagation()}>
                                    <Button
                                      variant="contained"
                                      sx={primaryBtnSx}
                                      onClick={() => handleSave(item.ApplicationID)}
                                      disabled={!editedData[item.ApplicationID] || savingId === item.ApplicationID}
                                    >
                                      {savingId === item.ApplicationID ? (
                                        <CircularProgress size={18} sx={{ color: "white" }} />
                                      ) : (
                                        "Save"
                                      )}
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      )}
                    </TableContainer>
                  </Box>
                </Paper>

                {expandedItem && (
                  <Box sx={{ mt: 2 }}>
                    <Collapse in timeout="auto">
                      <Stack spacing={2}>
                        <Paper variant="outlined" sx={{ ...sectionSx }}>
                          <Box sx={{ pl: 1 }}>
                            <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>
                              Application
                            </Typography>
                            <Divider sx={{ mb: 2 }} />

                            <Box sx={fixedGridSx(4)}>
                              {renderField("Application ID", expandedItem.ApplicationID)}
                              {renderField("Full Name", expandedItem.FullName)}
                              {renderField("Submitted Date", formatDateOnly(expandedItem.ApplicationSubmittedDateTime))}
                              {renderField("Application Status", expandedItem.ApplicationStatus)}
                            </Box>
                          </Box>
                        </Paper>

                        <Paper variant="outlined" sx={{ ...sectionSx }}>
                          <Box sx={{ pl: 1 }}>
                            <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>
                              Student Details
                            </Typography>
                            <Divider sx={{ mb: 2 }} />

                            <Box sx={fixedGridSx(5)}>
                              {renderField("Campus Code", expandedItem.CampusCode)}
                              {renderField("SubCampus Code", expandedItem.SubCampusCode)}
                              {renderField("Degree Code", expandedItem.DegreeCode)}
                              {renderField("Branch Code", expandedItem.BranchCode)}
                              {renderField("Gender", expandedItem.Gender)}
                              {renderField("DOB", formatDateOnly(expandedItem.DateOfBirth))}
                              {renderField("Blood Group", expandedItem.BloodGroup)}
                              {renderField("Aadhar", expandedItem.AadharNumber)}
                              {renderField("Phone", expandedItem.PhoneNumber)}
                              {renderField("Email", expandedItem.EmailAddress)}
                              {renderField("Religion", expandedItem.Religion)}
                              {renderField("Caste", expandedItem.Caste)}
                              {renderField("SubCaste", expandedItem.SubCaste)}
                              {renderField("Reference Name", expandedItem.ReferenceName)}
                              {renderField("Reference Phone", expandedItem.ReferencePhoneNumber)}
                            </Box>
                          </Box>
                        </Paper>

                        <Paper variant="outlined" sx={{ ...sectionSx }}>
                          <Box sx={{ pl: 1 }}>
                            <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>
                              Address
                            </Typography>
                            <Divider sx={{ mb: 2 }} />

                            <Box sx={fixedGridSx(7)}>
                              {renderField("Address 1", expandedItem.AddressLine1)}
                              {renderField("Address 2", expandedItem.AddressLine2)}
                              {renderField("Post Office", expandedItem.PostOfficeName)}
                              {renderField("City", expandedItem.City)}
                              {renderField("District", expandedItem.District)}
                              {renderField("State", expandedItem.State)}
                              {renderField("Zip", expandedItem.Zip)}
                            </Box>
                          </Box>
                        </Paper>

                        <Paper variant="outlined" sx={{ ...sectionSx }}>
                          <Box sx={{ pl: 1 }}>
                            <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>
                              Family Details
                            </Typography>
                            <Divider sx={{ mb: 2 }} />

                            <Box sx={fixedGridSx(5)}>
                              {renderField("Father Name", expandedItem.FatherFullName)}
                              {renderField("Father Aadhar", expandedItem.FatherAadharNumber)}
                              {renderField("Father Qualification", expandedItem.FatherQualification)}
                              {renderField("Father Occupation", expandedItem.FatherOccupation)}
                              {renderField("Father Phone", expandedItem.FatherPhoneNumber)}
                              {renderField("Mother Name", expandedItem.MotherFullName)}
                              {renderField("Mother Aadhar", expandedItem.MotherAadharNumber)}
                              {renderField("Mother Qualification", expandedItem.MotherQualification)}
                              {renderField("Mother Occupation", expandedItem.MotherOccupation)}
                              {renderField("Mother Phone", expandedItem.MotherPhoneNumber)}
                            </Box>
                          </Box>
                        </Paper>

                        <Paper variant="outlined" sx={{ ...sectionSx }}>
                          <Box sx={{ pl: 1 }}>
                            <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>
                              Education Details
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                            {renderEducationCards(expandedItem.EducationDetails || [])}
                          </Box>
                        </Paper>

                        <Paper variant="outlined" sx={{ ...sectionSx }}>
                          <Box sx={{ pl: 1 }}>
                            <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>
                              Competitive / Entrance Exams
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                            {renderCompetitiveExamCards(expandedItem.CompetitiveExams || [])}
                          </Box>
                        </Paper>

                        <Paper variant="outlined" sx={{ ...sectionSx }}>
                          <Box sx={{ pl: 1 }}>
                            <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>
                              Verified Documents
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                            {renderDocumentCards(expandedItem.DocumentDetails || [])}
                          </Box>
                        </Paper>
                      </Stack>
                    </Collapse>
                  </Box>
                )}
              </Box>
            </Paper>

            <Snackbar
              open={toast.open}
              autoHideDuration={3000}
              onClose={() => setToast((p) => ({ ...p, open: false }))}
              anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
              <Alert
                severity={toast.type === "error" ? "error" : toast.type === "info" ? "info" : "success"}
                sx={{ borderRadius: 2, border: "1px solid rgba(25,118,210,0.18)" }}
                onClose={() => setToast((p) => ({ ...p, open: false }))}
              >
                {toast.msg}
              </Alert>
            </Snackbar>
          </Box>
        </Paper>
      </Box>
    </LocalizationProvider>
  );
};

export default ApproverStudentApplication;